import { Container, Graphics, Rectangle, Text, type FederatedPointerEvent } from 'pixi.js'
import { DropShadowFilter } from '@pixi/filter-drop-shadow'
import { store } from '@/store/store'
import { selectCrashHistory } from '@/store/slices/crashSlice'

export class CrashHistoryBar extends Container {
	private maskGraphics!: Graphics
	private bg!: Graphics
	private scrollContainer!: Container
	private panelWidth = 0
	private panelHeight = 0
	private leftPadding = 24
	private rightPadding = 24
	private contentWidth = 0
	private scrollX = 0
	private isDragging = false
	private dragPointerStart = 0
	private dragScrollStart = 0
	private lastHistory: number[] = []
	private unsubscribeStore: (() => void) | null = null

	constructor() {
		super()
		this.init()
		this.subscribeToStore()
	}

	destroy(options?: boolean | { children?: boolean; texture?: boolean; baseTexture?: boolean }) {
		this.unsubscribeStore?.()
		this.unsubscribeStore = null
		this.off('pointerdown', this.onPointerDown)
		this.off('globalpointermove', this.onPointerMove)
		this.off('pointerup', this.onPointerUp)
		this.off('pointerupoutside', this.onPointerUp)
		this.off('pointercancel', this.onPointerUp)
		super.destroy(options)
	}

	private init() {
		this.bg = new Graphics()
		this.maskGraphics = new Graphics()
		this.scrollContainer = new Container()
		this.addChild(this.bg, this.maskGraphics, this.scrollContainer)
		this.mask = this.maskGraphics

		this.eventMode = 'static'
		this.cursor = 'grab'
		this.on('pointerdown', this.onPointerDown)
		this.on('globalpointermove', this.onPointerMove)
		this.on('pointerup', this.onPointerUp)
		this.on('pointerupoutside', this.onPointerUp)
		this.on('pointercancel', this.onPointerUp)
	}

	private readonly onPointerDown = (event: FederatedPointerEvent) => {
		if (this.contentWidth <= this.panelWidth) {
			return
		}

		this.isDragging = true
		this.dragPointerStart = event.globalX
		this.dragScrollStart = this.scrollX
		this.cursor = 'grabbing'
	}

	private readonly onPointerMove = (event: FederatedPointerEvent) => {
		if (!this.isDragging) {
			return
		}

		this.scrollX = this.dragScrollStart + (event.globalX - this.dragPointerStart)
		this.applyScroll()
	}

	private readonly onPointerUp = () => {
		if (!this.isDragging) {
			return
		}

		this.isDragging = false
		this.cursor = 'grab'
	}

	private subscribeToStore() {
		this.lastHistory = selectCrashHistory(store.getState())
		if (this.lastHistory.length) {
			this.renderHistory(this.lastHistory)
		}

		this.unsubscribeStore = store.subscribe(() => {
			const history = selectCrashHistory(store.getState())
			if (history === this.lastHistory) {
				return
			}

			const previousLast = this.lastHistory[this.lastHistory.length - 1]
			const previousLength = this.lastHistory.length
			this.lastHistory = history
			this.renderHistory(history, history[history.length - 1] !== previousLast || history.length !== previousLength)
		})
	}

	private getColor(value: number) {
		if (value > 50) return 0xeab308
		if (value > 10) return 0x3b82f6
		if (value > 2) return 0x22c55e
		return 0xef4444
	}

	private applyScroll() {
		const minScroll = Math.min(0, this.panelWidth - this.contentWidth)
		this.scrollX = Math.max(minScroll, Math.min(0, this.scrollX))
		this.scrollContainer.x = this.scrollX
	}

	private scrollToEnd() {
		this.scrollX = Math.min(0, this.panelWidth - this.contentWidth)
		this.applyScroll()
	}

	private renderHistory(history: number[], resetScroll = true) {
		this.scrollContainer.removeChildren()

		let x = this.leftPadding

		history.forEach((value) => {
			const txt = new Text({
				text: `${Number(value).toFixed(2)}x`,
				style: {
					fill: 0xffffff,
					fontSize: 18,
					fontWeight: '600',
				},
			})

			const padX = 16
			const pillW = txt.width + padX * 2
			const pillH = txt.height + 6
			const r = pillH / 2
			const pill = new Graphics()
			const baseColor = this.getColor(value)

			pill.roundRect(0, 0, pillW, pillH, r).fill({ color: baseColor })
			pill.roundRect(0, 0, pillW, pillH, r).fill({ color: 0xffffff, alpha: 0.05 })
			pill.y = this.panelHeight / 2 - pillH / 2
			txt.y = pill.y + pillH / 2 - txt.height / 2
			pill.x = x
			txt.x = x + pillW / 2 - txt.width / 2

			this.scrollContainer.addChild(pill, txt)
			x += pillW + 12
		})

		this.contentWidth = history.length ? x - 12 + this.rightPadding : 0

		if (resetScroll) {
			this.scrollToEnd()
		} else {
			this.applyScroll()
		}
	}

	private layout(width?: number, height?: number) {
		if (!width || !height) {
			return
		}

		this.panelWidth = width
		this.panelHeight = height
		this.hitArea = new Rectangle(0, 0, width, height)

		this.maskGraphics.clear().roundRect(0, 0, width, height, 20).fill(0xffffff)
		this.bg
			.clear()
			.roundRect(0, 0, width, height, 20)
			.fill({ color: 0x38c4ff, alpha: 0.95 })
			.roundRect(0, 0, width, height, 20)
			.stroke({ color: 0xffffff, alpha: 0.18, width: 1.5 })
			.roundRect(1, 1, width - 2, height - 2, 20)
			.stroke({ color: 0xffffff, alpha: 0.35, width: 1 })

		this.bg.filters = [
			new DropShadowFilter({
				color: 0x000000,
				alpha: 0.45,
				blur: 18,
				offset: { x: 0, y: 8 },
			}) as never,
		]
	}

	resize(width: number, height: number) {
		this.layout(width, height)
		if (this.lastHistory.length) {
			this.renderHistory(this.lastHistory, false)
		}
	}
}
